import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Hexagon, Loader2, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(u.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-bg min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500">
            <Hexagon className="h-6 w-6 text-slate-900" />
          </span>
          <div className="font-display font-extrabold text-xl text-white">Rightwayp2p</div>
        </Link>
        <div className="glass rounded-3xl p-8">
          <h1 className="font-display text-2xl font-bold text-white">Member Login</h1>
          <p className="text-sm text-slate-400 mt-1 mb-6">Access your wallet & network dashboard</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400 font-mono">Email or User ID</label>
              <input data-testid="login-email" type="text" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white outline-none focus:border-emerald-500/50" placeholder="you@example.com or SO294F33F6" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400 font-mono">Password</label>
              <div className="relative mt-1.5">
                <input data-testid="login-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 pr-12 text-white outline-none focus:border-emerald-500/50" placeholder="••••••••" />
                <button type="button" data-testid="login-password-toggle" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors">
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <button data-testid="login-submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-900 font-semibold py-3 rounded-xl transition-colors">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Login
            </button>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            No account? <Link to="/register" className="text-emerald-400 hover:text-emerald-300 font-medium">Register</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
